import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { DELIVERY_MODELS } from "@/data/site";

// Short button-label for each delivery model, used on the compact/pill variant.
const SHORT_LABEL = {
  "consulting-advisory": "Consulting",
  "fractional-managed-services": "Fractional",
  "workforce-solutions": "Workforce",
  "career-professional-development": "Career Services",
};

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

/**
 * Delivery-model badges, pills that tell the buyer HOW they can engage
 * with a given practice (Consulting, Fractional, Workforce, or Career Services).
 *
 * Props:
 *   slugs  , array of DELIVERY_MODELS slugs applicable to this service
 *   variant, "hero" (large, on service-detail hero), "inline" (compact, on cards)
 *   linkable, when true, each badge is a <Link>. Default true. Pass false when
 *              the badges live INSIDE another <Link> to keep the HTML valid.
 *   testid , data-testid prefix
 */
export default function DeliveryBadges({ slugs = [], variant = "inline", linkable = true, testid = "delivery-badges", onClick }) {
  const items = (slugs || [])
    .map((s) => DELIVERY_MODELS.find((m) => m.slug === s))
    .filter(Boolean);
  if (items.length === 0) return null;

  const isHero = variant === "hero";
  const wrapCls = isHero
    ? "flex flex-wrap items-center gap-3"
    : "flex flex-wrap items-center gap-1.5";

  return (
    <div className={wrapCls} data-testid={testid}>
      {isHero && (
        <span className="text-[10px] font-700 uppercase tracking-[0.16em] text-navy/50 mr-1">
          Delivered as
        </span>
      )}
      {items.map((m) => {
        const target = m.slug === "career-professional-development" ? "/career-services" : "/services#delivery-models";
        const label = SHORT_LABEL[m.slug] || m.title;
        const isB2C = m.slug === "career-professional-development";
        const heroCls = `group inline-flex items-center gap-2 px-3.5 py-2 border transition-colors ${
          isB2C
            ? "border-gold bg-gold/10 text-navy hover:bg-gold hover:text-navy"
            : "border-navy/20 bg-white text-navy hover:border-navy hover:bg-navy hover:text-white"
        }`;
        const inlineCls = `inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-700 uppercase tracking-wider transition-colors ${
          isB2C
            ? "border-gold text-gold bg-gold/5 hover:bg-gold hover:text-navy"
            : "border-navy/20 text-navy bg-lightgray hover:border-navy hover:bg-navy hover:text-white"
        }`;

        if (isHero) {
          const inner = (
            <>
              <span className={`w-6 h-6 flex items-center justify-center ${isB2C ? "bg-gold text-navy" : "bg-navy text-gold group-hover:bg-white group-hover:text-navy"} transition-colors`}>
                <Icon name={m.icon} className="w-3.5 h-3.5" strokeWidth={1.75} />
              </span>
              <span className="font-display font-700 text-xs uppercase tracking-wider">{label}</span>
              <span className="font-display font-800 text-[10px] opacity-60">{m.id}</span>
            </>
          );
          return linkable ? (
            <Link key={m.slug} to={target} onClick={onClick} data-testid={`${testid}-${m.slug}`} title={`${m.title} · ${m.tagline}`} className={heroCls}>{inner}</Link>
          ) : (
            <span key={m.slug} data-testid={`${testid}-${m.slug}`} title={`${m.title} · ${m.tagline}`} className={heroCls}>{inner}</span>
          );
        }

        // Compact pill
        const inner = (
          <>
            <Icon name={m.icon} className="w-3 h-3" strokeWidth={1.75} />
            {label}
          </>
        );
        return linkable ? (
          <Link key={m.slug} to={target} onClick={onClick} data-testid={`${testid}-${m.slug}`} title={`${m.title} · ${m.tagline}`} className={inlineCls}>{inner}</Link>
        ) : (
          <span key={m.slug} data-testid={`${testid}-${m.slug}`} title={`${m.title} · ${m.tagline}`} className={inlineCls}>{inner}</span>
        );
      })}
    </div>
  );
}
